import { Dispatch, FormEventHandler, SyntheticEvent, useCallback, useState } from 'react';
import { SPECIALTY_COVID_19_VACCINATIONS } from '../../../../bookings/constants';
import { useSearchFilters } from '../../../../common/components/SearchHeader/filters/useSearchFilters';
import { FAST_USER_INTERACTION_DEBOUNCE } from '../../../../common/constants';
import { useDebouncedValue } from '../../../../common/hooks/useDebounceValue';
import { useHannibalQuery } from '../../../../common/schema/hannibal/HannibalContext';
import { SpecialtyFilterOptionsDocument } from '../../../../common/schema/hannibal/types.generated';
import { useMobile } from '../../../../common/services/screenSize/ScreenSizeService';
import { useVariables } from '../../../useSearchQuery/useVariables';
import {
  Error,
  FilterTextInput,
  SpecialtyFilterBox,
  SpecialtyFilterHeader,
  SpecialtyList,
  Title,
} from './SpecialtyFilter.styles';
import { SpecialtyListItem } from './SpecialtyListItem';

export interface Props {
  value?: string;
  onChange?: Dispatch<string>;
}

export const SpecialtyFilterContent = ({ onChange: onSpecialtySelected, value }: Props) => {
  const [rawQuery, setQuery] = useState('');
  const query = useDebouncedValue(rawQuery, FAST_USER_INTERACTION_DEBOUNCE);

  const { data, loading } = useSpecialtyOptions(value, query);

  const handleTextChange = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    setQuery(e.currentTarget.value);
  }, []);

  const handleKeyboardSubmit: FormEventHandler = useCallback(
    ev => {
      ev.preventDefault();
      const firstSpecialty = data[0];
      if (firstSpecialty) onSpecialtySelected?.(firstSpecialty);
    },
    [data, onSpecialtySelected],
  );

  const isMobile = useMobile();

  return (
    <SpecialtyFilterBox>
      <SpecialtyFilterHeader>
        <Title>Specialty</Title>

        <form onSubmit={handleKeyboardSubmit}>
          {/* We acknowledge that using autofocus here may potentially reduce usability and accessibility. */}
          {/* eslint-disable jsx-a11y/no-autofocus */}
          <FilterTextInput
            onChange={handleTextChange}
            value={rawQuery}
            autoFocus={!isMobile}
            placeholder="Start searching for a specialty"
          />
          {/* eslint-enable jsx-a11y/no-autofocus */}
        </form>
      </SpecialtyFilterHeader>

      {query && data.length === 0 && !loading && (
        <Error>
          <p>Sorry, there are no results for "{query}".</p>

          <p>Try another specialty.</p>
        </Error>
      )}

      <SpecialtyList>
        {data.map((choice, index) => (
          <SpecialtyListItem key={index} onChoose={onSpecialtySelected} choice={choice} />
        ))}
      </SpecialtyList>
    </SpecialtyFilterBox>
  );
};

const useSpecialtyOptions = (currentSpecialty: string | undefined, userQuery: string) => {
  const [filters] = useSearchFilters();

  const queryState = useHannibalQuery(SpecialtyFilterOptionsDocument, {
    variables: { ...useVariables(filters), filterInnerQuery: userQuery || ' ' },
  });

  const scopedValues =
    queryState.data?.searchProfiles.facetsWithCounts.specialists.values?.map(it => it.term) ?? [];

  const globalValues = queryState.data?.searchSpecialtiesByName.specialties.map(
    it => it.specialist,
  );

  const shouldSearchGlobally = Boolean(currentSpecialty || userQuery) || scopedValues.length === 0;

  const rawData = shouldSearchGlobally && globalValues ? globalValues : scopedValues;

  const data = userQuery ? rawData : promoteCovaxSpecialty(rawData);

  return { data, loading: queryState.loading };
};

const promoteCovaxSpecialty = (specialties: string[]) => [
  SPECIALTY_COVID_19_VACCINATIONS,
  ...specialties.filter(it => it !== SPECIALTY_COVID_19_VACCINATIONS),
];
